import React, { useState } from 'react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { Mail, LifeBuoy } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('Sending...');

    try {
      const response = await fetch(API_ENDPOINTS.CONTACT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('Message sent successfully!');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setStatus('Failed to send message. Please try again.');
    } finally {
        setTimeout(() => setStatus(''), 3000);
    }
  };

  return (
    <div className="py-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">Get In Touch</h1>
          <p className="mt-4 text-lg text-slate-600">We'd love to hear from you. Send us a message for any inquiries.</p>
        </div>

        <Card className="max-w-4xl mx-auto p-0">
          <div className="grid md:grid-cols-2">
            {/* Info Panel */}
            <div className="p-8 md:p-10 bg-emerald-500/10 rounded-t-xl md:rounded-l-xl md:rounded-tr-none">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">Contact Information</h2>
              <p className="text-slate-600 mb-8">
                Have a question or want to give feedback? Fill out the form or email us directly.
              </p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Mail className="w-6 h-6 text-emerald-500" />
                  <div>
                    <div className="font-semibold text-slate-700">General Inquiries</div>
                    <a href="mailto:hello@ThreadIt.ai" className="text-slate-600 hover:text-emerald-600">hello@ThreadIt.ai</a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <LifeBuoy className="w-6 h-6 text-emerald-500" />
                   <div>
                    <div className="font-semibold text-slate-700">Technical Support</div>
                    <a href="mailto:hello@ThreadIt.ai" className="text-slate-600 hover:text-emerald-600">hello@ThreadIt.ai</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                 <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-600 mb-2">Full Name</label>
                    <input
                      type="text" name="name" id="name" value={formData.name} onChange={handleChange} required
                      className="w-full bg-slate-100/50 border border-slate-300 rounded-md px-4 py-2 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                   <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-600 mb-2">Email Address</label>
                    <input
                      type="email" name="email" id="email" value={formData.email} onChange={handleChange} required
                      className="w-full bg-slate-100/50 border border-slate-300 rounded-md px-4 py-2 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-600 mb-2">Subject</label>
                  <input
                    type="text" name="subject" id="subject" value={formData.subject} onChange={handleChange} required
                    className="w-full bg-slate-100/50 border border-slate-300 rounded-md px-4 py-2 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-600 mb-2">Message</label>
                  <textarea
                    name="message" id="message" rows={5} value={formData.message} onChange={handleChange} required
                    className="w-full bg-slate-100/50 border border-slate-300 rounded-md px-4 py-2 text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  ></textarea>
                </div>
                <div className="text-left">
                  <Button type="submit" className="px-8 py-3">
                    Send Message
                  </Button>
                </div>
                {status && <p className="text-center mt-4 text-emerald-500">{status}</p>}
              </form>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ContactPage;